/**
 * Client-side mirror of the server session (PRD §4.1, §8).
 *
 * The server decides everything; this only reflects `GET /api/auth/session` so the shell knows
 * which navigation to draw and where to send someone after they sign in.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { homePath, type SessionResponse } from "@/shared/schemas/auth";

export type SessionUser = NonNullable<SessionResponse["user"]>;
export type Role = SessionUser["role"];

export const SESSION_QUERY_KEY = ["session"] as const;

export function useSession() {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => api.get<SessionResponse>("/auth/session"),
    staleTime: 60_000,
  });

  return {
    user: query.data?.user ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SessionResponse>("/auth/logout"),
    onSuccess: () => {
      queryClient.setQueryData(SESSION_QUERY_KEY, { user: null } satisfies SessionResponse);
      queryClient.clear();
    },
  });
}

/** Where a signed-in person belongs when they land on `/` or `/auth`. */
export function homePathFor(user: SessionUser): string {
  return homePath(user);
}
